// T5 — searchable How to Use reference. The flat card stack became a
// three-part reference: a live search box filtering by page name or
// summary, a sticky grouped table of contents (lg+ widths only), and
// accordion cards that expand to numbered how-to steps. All content
// still derives from the nav registry (`visibleNavItems`) — adding a
// sidebar destination automatically adds its reference card here with
// zero changes to this view.
import { useEffect, useMemo, useState } from "react";
import { Search as SearchIcon } from "lucide-react";
import { NAV_SECTION_LABELS, visibleNavItems } from "@/lib/navItems";
import type { NavItem, NavSection } from "@/lib/navItems";
import type { UserOut } from "@/lib/api";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { AssessmentEmptyState } from "@/components/AssessmentEmptyState";

const SECTIONS: readonly NavSection[] = ["top", "bottom"];

export function HowToUseView({ me }: { me: UserOut }) {
  const items = useMemo(() => visibleNavItems(me), [me]);

  const [searchQuery, setSearchQuery] = useState("");
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(
    () => new Set(items.slice(0, 2).map((item) => item.key)),
  );

  // Header clicks keep an independent additive toggle — several cards
  // can be open at once when expanded from the cards themselves.
  const setCardOpen = (key: string, open: boolean) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (open) {
        next.add(key);
      } else {
        next.delete(key);
      }
      return next;
    });
  };

  // Clicking a table-of-contents entry expands only that section and
  // collapses the rest (exclusive single-open), so the TOC always
  // lands the reader on exactly the page they picked.
  const selectFromToc = (key: string) => {
    setExpandedKeys(new Set([key]));
  };

  // Keep the expanded state valid if the visible items change (e.g. a
  // role/profile refresh hides a previously-open page). If nothing that
  // is currently open still exists, fall back to opening the first item.
  useEffect(() => {
    setExpandedKeys((prev) => {
      const valid = new Set(
        [...prev].filter((key) => items.some((item) => item.key === key)),
      );
      if (valid.size > 0) return valid;
      const first = items[0];
      return new Set(first ? [first.key] : []);
    });
  }, [items]);

  const query = searchQuery.trim().toLowerCase();
  const filtered = query
    ? items.filter(
        (item) =>
          item.label.toLowerCase().includes(query) ||
          item.usage.summary.toLowerCase().includes(query) ||
          item.usage.howToUse.some((step) => step.toLowerCase().includes(query)),
      )
    : items;

  const grouped = SECTIONS.map((section) => ({
    section,
    items: filtered.filter((item) => item.section === section),
  }));

  return (
    <div className="space-y-8">
      <div>
        <h1
          className="text-2xl font-semibold text-foreground"
          style={{ fontFamily: "var(--app-font-serif)" }}
        >
          How to use ConvoAI
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          A short tour of every page available to you from the sidebar.
        </p>
        <div className="relative mt-4 max-w-xl">
          <SearchIcon
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <input
            type="text"
            placeholder="Search for a page or feature…"
            className="w-full rounded border border-input bg-card py-2 pl-9 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-label="Search for a page or feature"
          />
        </div>
      </div>

      <div className="flex items-start gap-12">
        {/* Left: table of contents (hidden below lg; cards stay usable) */}
        <nav
          aria-label="Table of contents"
          className="sticky top-6 hidden w-64 shrink-0 lg:block"
        >
          <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Table of contents
          </h2>
          {grouped.map(({ section, items: groupItems }) =>
            groupItems.length > 0 ? (
              <div
                key={section}
                className="mb-6 last:mb-0"
                data-testid={`howto-toc-group-${section}`}
              >
                <h3 className="mb-2 text-sm font-semibold text-foreground">
                  {NAV_SECTION_LABELS[section]}
                </h3>
                <ul className="ml-1 flex flex-col gap-1 border-l border-border pl-3">
                  {groupItems.map((item) => {
                    const active = expandedKeys.has(item.key);
                    return (
                      <li key={item.key}>
                        <a
                          href={`#howto-${item.key}`}
                          onClick={() => selectFromToc(item.key)}
                          className={`text-sm transition-colors ${
                            active
                              ? "font-medium text-primary"
                              : "text-muted-foreground hover:text-primary"
                          }`}
                        >
                          {item.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ) : null,
          )}
        </nav>

        {/* Right: accordion cards */}
        <div className="flex min-w-0 flex-1 flex-col gap-6 pb-12">
          {filtered.length === 0 ? (
            <AssessmentEmptyState
              icon={<SearchIcon className="h-5 w-5" aria-hidden="true" />}
              headline={`No matching pages found for "${searchQuery.trim()}"`}
              subline="Try a different word, or search by the page name you see in the sidebar."
            />
          ) : (
            grouped.map(({ section, items: groupItems }) =>
              groupItems.length > 0 ? (
                <div key={section} data-testid={`howto-group-${section}`}>
                  <h2 className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground lg:hidden">
                    {NAV_SECTION_LABELS[section]}
                  </h2>
                  <div className="flex flex-col gap-3">
                    {groupItems.map((item) => (
                      <HowToCard
                        key={item.key}
                        item={item}
                        open={expandedKeys.has(item.key)}
                        onOpenChange={(open) => setCardOpen(item.key, open)}
                      />
                    ))}
                  </div>
                </div>
              ) : null,
            )
          )}
        </div>
      </div>
    </div>
  );
}

function HowToCard({
  item,
  open,
  onOpenChange,
}: {
  item: NavItem;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <div id={`howto-${item.key}`} data-testid="howto-card">
      <CollapsibleSection
        open={open}
        onOpenChange={onOpenChange}
        headerClassName="hover-elevate rounded-lg"
        title={
          <span className="flex items-start gap-3">
            <span
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
              aria-hidden="true"
            >
              {item.icon}
            </span>
            <span className="min-w-0 flex-1">
              <span
                className="block font-semibold text-foreground"
                style={{ fontFamily: "var(--app-font-serif)" }}
                data-testid="howto-card-label"
              >
                {item.label}
              </span>
              <span className="mt-0.5 block text-sm font-normal text-muted-foreground">
                {item.usage.summary}
              </span>
            </span>
          </span>
        }
      >
        <div className="border-t border-border pt-3">
          <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            How to use it
          </h3>
          <ol className="mt-2 list-decimal space-y-1.5 pl-5 text-sm text-foreground">
            {item.usage.howToUse.map((step, idx) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </div>
      </CollapsibleSection>
    </div>
  );
}
